import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useOAuthFlow } from '@/hooks/useOAuthFlow';

interface BlueskyConnectFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const BlueskyConnectForm = ({ onSuccess, onCancel }: BlueskyConnectFormProps) => {
  const [handle, setHandle] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const { connectBlueskyAccount, isConnecting } = useOAuthFlow({
    onSuccess: () => onSuccess?.(),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await connectBlueskyAccount(handle, appPassword);
      setHandle('');
      setAppPassword('');
    } catch {
      // toast already shown inside the hook
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 sm:p-6 bg-background border border-border rounded-lg">
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-foreground">Connect Bluesky</h3>
        <p className="text-xs text-muted-foreground">
          Bluesky uses an app password instead of OAuth. Create one at{' '}
          <a
            href="https://bsky.app/settings/app-passwords"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            bsky.app/settings/app-passwords
          </a>
          . Your app password is used once to create a session, then discarded.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="bluesky-handle">Handle</Label>
        <Input
          id="bluesky-handle"
          type="text"
          autoComplete="off"
          placeholder="you.bsky.social"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="bluesky-app-password">App password</Label>
        <Input
          id="bluesky-app-password"
          type="password"
          autoComplete="off"
          placeholder="xxxx-xxxx-xxxx-xxxx"
          value={appPassword}
          onChange={(e) => setAppPassword(e.target.value)}
          required
        />
      </div>

      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isConnecting.bluesky}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isConnecting.bluesky || !handle || !appPassword}>
          {isConnecting.bluesky ? 'Connecting...' : 'Connect'}
        </Button>
      </div>
    </form>
  );
};
